import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { EmailAuthProvider, getAuth, onAuthStateChanged, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import { doc, getDoc, getFirestore, updateDoc } from 'firebase/firestore';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { Button, Form, Card, Container, Row, Col, InputGroup, Spinner } from 'react-bootstrap';
import { FaCamera, FaUser, FaPhone, FaGlobe, FaCity, FaGraduationCap, FaCalendarAlt, FaChalkboardTeacher, FaEye, FaEyeSlash, FaKey } from 'react-icons/fa';
import Swal from 'sweetalert2';
import './EditProfileView.scss';

function EditProfileView() {
    const navigate = useNavigate();
    const auth = getAuth();
    const db = getFirestore();
    const storage = getStorage();
    const [currentUser, setCurrentUser] = useState({});
    const [loading, setLoading] = useState(false);
    const [imageURL, setImageURL] = useState(null);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [changingPassword, setChangingPassword] = useState(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, user => {
            if (user) {
                const userRef = doc(db, "users", user.uid);
                getDoc(userRef).then(docSnap => {
                    if (docSnap.exists()) {
                        setCurrentUser({ uid: user.uid, ...docSnap.data() });
                        setImageURL(docSnap.data().profilePicture || null);
                    }
                });
            } else {
                navigate('/login');
            }
        });
        return unsubscribe;
    }, [auth, db, navigate]);

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (file) {
            try {
                setLoading(true);
                const fileRef = storageRef(storage, `profilePictures/${currentUser.uid}`);
                const snapshot = await uploadBytes(fileRef, file);
                const downloadURL = await getDownloadURL(snapshot.ref);
                setCurrentUser({ ...currentUser, profilePicture: downloadURL });
                await updateDoc(doc(db, "users", currentUser.uid), { profilePicture: downloadURL });
                Swal.fire({
                    icon: 'success',
                    title: '¡Éxito!',
                    text: 'Foto de perfil actualizada correctamente',
                    confirmButtonColor: '#3085d6'
                });
            } catch (error) {
                console.error("Error al actualizar la foto:", error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'No se pudo actualizar la foto de perfil',
                    confirmButtonColor: '#3085d6'
                });
            } finally {
                setLoading(false);
            }
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCurrentUser({ ...currentUser, [name]: value });
    };

    const handleSaveChanges = async (event) => {
        event.preventDefault();
        const { uid, username, phone, country, city } = currentUser;

        if (uid) {
            setLoading(true);
            const updateData = {};
            if (username !== undefined) updateData.username = username;
            if (phone !== undefined) updateData.phone = phone;
            if (country !== undefined) updateData.country = country;
            if (city !== undefined) updateData.city = city;

            try {
                const userRef = doc(db, "users", uid);
                await updateDoc(userRef, updateData);
                Swal.fire({
                    icon: 'success',
                    title: '¡Éxito!',
                    text: 'Información actualizada correctamente',
                    confirmButtonColor: '#3085d6'
                });
            } catch (error) {
                console.error("Error updating profile:", error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'No se pudo actualizar la información',
                    confirmButtonColor: '#3085d6'
                });
            } finally {
                setLoading(false);
            }
        }
    };

    const handleChangePassword = async (event) => {
        event.preventDefault();

        if (!currentPassword || !newPassword || !confirmPassword) {
            await Swal.fire({
                icon: 'warning',
                title: 'Campos incompletos',
                text: 'Completa tu contraseña actual, la nueva y su confirmación.',
            });
            return;
        }

        if (newPassword.length < 6) {
            await Swal.fire({
                icon: 'warning',
                title: 'Contraseña inválida',
                text: 'La nueva contraseña debe tener al menos 6 caracteres.',
            });
            return;
        }

        if (newPassword !== confirmPassword) {
            await Swal.fire({
                icon: 'warning',
                title: 'Las contraseñas no coinciden',
                text: 'Verifica la confirmación de tu nueva contraseña.',
            });
            return;
        }

        const authUser = auth.currentUser;
        if (!authUser?.email) {
            await Swal.fire({
                icon: 'error',
                title: 'No se pudo cambiar la contraseña',
                text: 'Tu sesión no tiene un correo asociado.',
            });
            return;
        }

        try {
            setChangingPassword(true);
            const credential = EmailAuthProvider.credential(authUser.email, currentPassword);
            await reauthenticateWithCredential(authUser, credential);
            await updatePassword(authUser, newPassword);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            await Swal.fire({
                icon: 'success',
                title: 'Contraseña actualizada',
                text: 'Tu nueva contraseña ya está activa.',
            });
        } catch (error) {
            console.error('Error al cambiar la contraseña:', error);
            let errorMessage = 'No se pudo cambiar la contraseña. Inténtalo nuevamente.';
            if (error?.code === 'auth/invalid-credential' || error?.code === 'auth/wrong-password') {
                errorMessage = 'Tu contraseña actual no es correcta.';
            } else if (error?.code === 'auth/weak-password') {
                errorMessage = 'La nueva contraseña es demasiado débil.';
            } else if (error?.code === 'auth/requires-recent-login') {
                errorMessage = 'Por seguridad, cierra sesión e inicia sesión nuevamente antes de cambiar tu contraseña.';
            }
            await Swal.fire({ icon: 'error', title: 'No se pudo cambiar la contraseña', text: errorMessage });
        } finally {
            setChangingPassword(false);
        }
    };

    return (
        <div className="edit-profile">
            <div className="edit-profile__navbar">
                <span className="edit-profile__navbar-title">Editar Perfil</span>
                <div className="edit-profile__navbar-actions">
                    <button className="btn" onClick={() => navigate(-1)}>Volver</button>
                </div>
            </div>
            <Container className="mt-4">
                <div className="edit-profile__header">
                    <h2>Mi Perfil</h2>
                </div>

                <Row>
                    <Col md={4}>
                        <Card className="edit-profile__profile-card edit-profile__glass">
                            <div className="position-relative d-inline-block">
                                <img
                                    src={currentUser.profilePicture || "/img/default-profile.jpg"}
                                    alt="Profile"
                                    className="edit-profile__profile-image"
                                />
                                <label className="edit-profile__camera-icon">
                                    <FaCamera />
                                    <input
                                        type="file"
                                        onChange={handleFileChange}
                                        accept="image/*"
                                    />
                                </label>
                            </div>
                            <div className="text-center mt-3">
                                <h3>{currentUser.name}</h3>
                                <p className="text-muted">@{currentUser.username}</p>
                            </div>
                        </Card>

                        <Card className="edit-profile__info-section edit-profile__glass">
                            <h3><FaGraduationCap className="me-2" />Información Académica</h3>
                            <div className="edit-profile__info-item">
                                <label><FaCalendarAlt className="me-2" />Fecha de Inscripción</label>
                                <p>{currentUser.inscripcion || 'No disponible'}</p>
                            </div>
                            <div className="edit-profile__info-item">
                                <label><FaChalkboardTeacher className="me-2" />Profesor Asignado</label>
                                <p>{currentUser.asignTeacher || 'Sin Asignar'}</p>
                            </div>
                            <div className="edit-profile__info-item">
                                <label><FaGraduationCap className="me-2" />Nivel Actual</label>
                                <p>{currentUser.level || 'No disponible'}</p>
                            </div>
                        </Card>
                    </Col>

                    <Col md={8}>
                        <Card className="edit-profile__info-section edit-profile__glass">
                            <h3><FaUser className="me-2" />Información Personal</h3>
                            <Form onSubmit={handleSaveChanges}>
                                <Row>
                                    <Col md={6}>
                                        <div className="edit-profile__info-item">
                                            <label><FaUser className="me-2" />Nombre de Usuario</label>
                                            <Form.Control
                                                type="text"
                                                name="username"
                                                value={currentUser.username || ''}
                                                onChange={handleInputChange}
                                                placeholder="Tu nombre de usuario"
                                            />
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="edit-profile__info-item">
                                            <label><FaPhone className="me-2" />Teléfono</label>
                                            <Form.Control
                                                type="tel"
                                                name="phone"
                                                value={currentUser.phone || ''}
                                                onChange={handleInputChange}
                                                placeholder="Tu número de teléfono"
                                            />
                                        </div>
                                    </Col>
                                </Row>

                                <Row className="mt-3">
                                    <Col md={6}>
                                        <div className="edit-profile__info-item">
                                            <label><FaGlobe className="me-2" />País</label>
                                            <Form.Control
                                                type="text"
                                                name="country"
                                                value={currentUser.country || ''}
                                                onChange={handleInputChange}
                                                placeholder="Tu país"
                                            />
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="edit-profile__info-item">
                                            <label><FaCity className="me-2" />Ciudad</label>
                                            <Form.Control
                                                type="text"
                                                name="city"
                                                value={currentUser.city || ''}
                                                onChange={handleInputChange}
                                                placeholder="Tu ciudad"
                                            />
                                        </div>
                                    </Col>
                                </Row>

                                <div className="edit-profile__actions">
                                    <Button
                                        variant="primary"
                                        type="submit"
                                        disabled={loading}
                                        className="px-4"
                                    >
                                        {loading ? 'Guardando...' : 'Guardar Cambios'}
                                    </Button>
                                </div>
                            </Form>
                        </Card>

                        <Card className="edit-profile__info-section edit-profile__glass">
                            <h3><FaKey className="me-2" />Seguridad</h3>
                            <Form onSubmit={handleChangePassword}>
                                <div className="edit-profile__info-item">
                                    <label>Contraseña actual</label>
                                    <InputGroup>
                                        <Form.Control
                                            type={showCurrentPassword ? 'text' : 'password'}
                                            value={currentPassword}
                                            onChange={(event) => setCurrentPassword(event.target.value)}
                                            autoComplete="current-password"
                                        />
                                        <Button
                                            type="button"
                                            variant="outline-secondary"
                                            aria-label={showCurrentPassword ? 'Ocultar contraseña actual' : 'Mostrar contraseña actual'}
                                            onClick={() => setShowCurrentPassword((value) => !value)}
                                        >
                                            {showCurrentPassword ? <FaEyeSlash /> : <FaEye />}
                                        </Button>
                                    </InputGroup>
                                </div>
                                <Row>
                                    <Col md={6}>
                                        <div className="edit-profile__info-item">
                                            <label>Nueva contraseña</label>
                                            <InputGroup>
                                                <Form.Control
                                                    type={showNewPassword ? 'text' : 'password'}
                                                    value={newPassword}
                                                    onChange={(event) => setNewPassword(event.target.value)}
                                                    autoComplete="new-password"
                                                />
                                                <Button
                                                    type="button"
                                                    variant="outline-secondary"
                                                    aria-label={showNewPassword ? 'Ocultar nueva contraseña' : 'Mostrar nueva contraseña'}
                                                    onClick={() => setShowNewPassword((value) => !value)}
                                                >
                                                    {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                                                </Button>
                                            </InputGroup>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="edit-profile__info-item">
                                            <label>Confirmar nueva contraseña</label>
                                            <InputGroup>
                                                <Form.Control
                                                    type={showConfirmPassword ? 'text' : 'password'}
                                                    value={confirmPassword}
                                                    onChange={(event) => setConfirmPassword(event.target.value)}
                                                    autoComplete="new-password"
                                                />
                                                <Button
                                                    type="button"
                                                    variant="outline-secondary"
                                                    aria-label={showConfirmPassword ? 'Ocultar confirmación de contraseña' : 'Mostrar confirmación de contraseña'}
                                                    onClick={() => setShowConfirmPassword((value) => !value)}
                                                >
                                                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                                </Button>
                                            </InputGroup>
                                        </div>
                                    </Col>
                                </Row>
                                <Form.Text className="text-muted">
                                    Puedes mostrar u ocultar los valores que estás escribiendo. Tu contraseña actual nunca se revela.
                                </Form.Text>
                                <div className="edit-profile__actions">
                                    <Button variant="primary" type="submit" disabled={changingPassword} className="px-4">
                                        {changingPassword ? <><Spinner animation="border" size="sm" /> Actualizando...</> : 'Cambiar contraseña'}
                                    </Button>
                                </div>
                            </Form>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
}

export { EditProfileView };
